import { type FC, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AppBar, Stack, Tab, Tabs } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { RefundsList } from "@/partner/features/refunds";
import { DisputesList } from "@/partner/features/disputes";
import { FilterProvider, useFilter } from "@/partner/shared/components/Filter";
import {
  FilterContext,
  type FilterContextValue,
} from "@/partner/shared/components/Filter/FilterContext";
import { ChargebacksFilter } from "./ChargebacksFilter";
import { type ChargebacksTab, parseChargebacksTab } from "../model/tabs";

// Возвраты/Диспуты — один пункт меню и одна страница с переключателем вкладок
// (?tab=refunds|disputes, как в VideoTabsMenu/StreamTabsMenu).
//
// План 2026-09-05-finance-pages-filters (Task 5): фильтр на странице один
// (один `FilterProvider` и одна карточка `<Filter>` над вкладками), поэтому
// переключение вкладки больше не стирает querystring целиком — общий фильтр
// (поиск, даты, продукты, провайдеры) переезжает с вкладки на вкладку. Не
// переезжает только статус: у вкладок разные enum'ы (`RefundStatus` /
// `DisputeStatus`), поэтому `filter[status]` и `page` при смене сбрасываются.
export const Chargebacks: FC = () => {
  return (
    <FilterProvider>
      <ChargebacksContent />
    </FilterProvider>
  );
};

// `FilterProvider.onClearFilter` делает `setSearchParams({})` — вместе с
// фильтром это стёрло бы и `?tab=`, выкинув пользователя с вкладки диспутов
// на возвраты. Подменяем только этот обработчик, сам провайдер (общий файл)
// не трогаем.
const ChargebacksFilterScope: FC<{
  tab: ChargebacksTab;
  children: ReactNode;
}> = ({ tab, children }) => {
  const filterContext = useFilter();
  const [, setSearchParams] = useSearchParams();

  const value: FilterContextValue = {
    ...filterContext,
    onClearFilter: () => setSearchParams(new URLSearchParams({ tab })),
  };

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
};

const ChargebacksContent: FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = parseChargebacksTab(searchParams.get("tab"));

  const items: { name: ChargebacksTab; label: string }[] = [
    { name: "refunds", label: t("chargebacks.tabs.refunds") },
    { name: "disputes", label: t("chargebacks.tabs.disputes") },
  ];

  const onSelectTab = (name: ChargebacksTab) => {
    // Повторный клик по активной вкладке не должен сбрасывать статус и страницу.
    if (name === tab) return;
    const next = new URLSearchParams(searchParams);
    next.set("tab", name);
    // Статус относится к конкретной вкладке, страница — к конкретной выборке.
    next.delete("filter[status]");
    next.delete("page");
    setSearchParams(next);
  };

  return (
    <ChargebacksFilterScope tab={tab}>
      <Stack spacing={1}>
        <ChargebacksFilter tab={tab} />
        <AppBar position="static" color="default" elevation={0}>
          <Tabs value={tab} scrollButtons="auto" variant="scrollable">
            {items.map(({ name, label }) => (
              <Tab
                key={name}
                value={name}
                label={label}
                onClick={() => onSelectTab(name)}
              />
            ))}
          </Tabs>
        </AppBar>
        {tab === "refunds" ? <RefundsList /> : <DisputesList />}
      </Stack>
    </ChargebacksFilterScope>
  );
};
